// ./src/utils/api/DisplayZoneService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Display } from "./DisplayService";
import { Venue } from "./VenueService";
import { Room } from "./RoomService";
import { PlayList } from "./PlayListService";

// GraphQL queries
const DISPLAYZONE_LIST_QUERY = gql`
  query DisplayZoneList(
    $filter: DisplayZoneFilter
    $params: PaginationAndSorting
  ) {
    displayZoneList(filter: $filter, params: $params) {
      code
      data {
        id
        displayId
        displayZoneId
        displayZoneType
        displayZoneTitle
        playListId
        fallbackPlayListId
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        createdAt
        createdBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAYZONE_GET_QUERY = gql`
  query DisplayZoneGet($id: ID) {
    displayZoneGet(id: $id) {
      code
      data {
        id
        displayId
        displayZoneId
        displayZoneType
        displayZoneTitle
        playListId
        fallbackPlayListId
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        createdAt
        createdBy
        display {
          id
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
        playList {
          id
          accountId
          name
          description
          status
        }
        venue {
          id
          name
          description
          status
        }
        room {
          id
          venueId
          name
          description
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAYZONE_GET_MANY_QUERY = gql`
  query DisplayZoneGetMany($ids: [ID]) {
    displayZoneGetMany(ids: $ids) {
      code
      data {
        id
        displayId
        displayZoneId
        displayZoneType
        displayZoneTitle
        playListId
        fallbackPlayListId
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        createdAt
        createdBy
        display {
          id
          themeId
          layoutId
          name
          description
          displayType
          roomId
          roomTypeId
          floor
          tag
          runningMessageId
          showPastHours
          showUpcomingHours
          emergencyContentBlockId
          status
        }
        playList {
          id
          accountId
          name
          description
          status
        }
        venue {
          id
          name
          description
          status
        }
        room {
          id
          venueId
          name
          description
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const DISPLAYZONE_CREATE_MUTATION = gql`
  mutation DisplayZoneCreate($params: DisplayZoneInput) {
    displayZoneCreate(params: $params) {
      code
      data {
        id
        displayId
        displayZoneId
        displayZoneType
        displayZoneTitle
        playListId
        fallbackPlayListId
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        createdAt
        createdBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAYZONE_UPDATE_MUTATION = gql`
  mutation DisplayZoneUpdate(
    $where: DisplayZoneWhereInput
    $params: DisplayZoneInput
  ) {
    displayZoneUpdate(where: $where, params: $params) {
      code
      data {
        id
        displayId
        displayZoneId
        displayZoneType
        displayZoneTitle
        playListId
        fallbackPlayListId
        scheduleType
        venueId
        roomId
        roomTypeId
        floor
        roomCategoryId
        eventCategoryId
        showPastHours
        showUpcomingHours
        createdAt
        createdBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAYZONE_REMOVE_MUTATION = gql`
  mutation DisplayZoneRemove($where: DisplayZoneWhereInput) {
    displayZoneRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAYZONE_REMOVE_MANY_MUTATION = gql`
  mutation DisplayZoneRemoveMany($where: [DisplayZoneWhereInput]) {
    displayZoneRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface DisplayZone {
  id?: string | undefined;
  displayId?: string | undefined;
  displayZoneId?: string | undefined;
  displayZoneType?: string | undefined;
  displayZoneTitle?: string | null | undefined;
  playListId?: string | undefined;
  fallbackPlayListId?: string | null | undefined;
  scheduleType?: string | undefined;
  venueId?: string | undefined;
  roomId?: string | undefined;
  roomTypeId?: string | undefined;
  floor?: string | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  showPastHours?: number | null | undefined;
  showUpcomingHours?: number | null | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  userCreated?: User;
  display?: Display;
  venue?: Venue;
  room?: Room;
  playList?: PlayList;
  fallbackPlayList?: PlayList;
}

export interface DisplayZoneInput {
  displayId?: string | null | undefined;
  displayZoneId?: string | null | undefined;
  displayZoneType?: string | null | undefined;
  displayZoneTitle?: string | null | undefined;
  playListId?: string | null | undefined;
  fallbackPlayListId?: string | null | undefined;
  scheduleType?: string | null | undefined;
  venueId?: string | null | undefined;
  roomId?: string | null | undefined;
  roomTypeId?: string | null | undefined;
  floor?: string | null | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  showPastHours?: number | null | undefined;
  showUpcomingHours?: number | null | undefined;
}

export interface DisplayZoneFilter {
  id?: string[] | null | undefined;
  displayId?: string[] | null | undefined;
  displayZoneId?: string[] | null | undefined;
  displayZoneType?: string[] | null | undefined;
  playListId?: string[] | null | undefined;
  venueId?: string[] | null | undefined;
  roomId?: string[] | null | undefined;
  roomTypeId?: string[] | null | undefined;
  floor?: string[] | null | undefined;
  roomCategoryId?: string | null | undefined;
  eventCategoryId?: string | null | undefined;
  status?: number[] | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
}

export interface DisplayZoneWhereInput {
  id?: string | undefined;
}

/**
 * DisplayZone service that handles all display zone-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayZoneService {
  /**
   * Get a list of display zones with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayZoneFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAYZONE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single display zone by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAYZONE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple display zones by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAYZONE_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new display zone
   */
  static async create(params: DisplayZoneInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAYZONE_CREATE_MUTATION,
      {
        params,
      },
    );
    return result;
  }

  /**
   * Update an existing display zone
   */
  static async update(
    where: DisplayZoneWhereInput,
    params: DisplayZoneInput,
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAYZONE_UPDATE_MUTATION,
      {
        where,
        params,
      },
    );
    return result;
  }

  /**
   * Remove/delete a display zone
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAYZONE_REMOVE_MUTATION,
      {
        where: { id },
      },
    );
    return result;
  }

  /**
   * Remove/delete multiple display zones
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      DISPLAYZONE_REMOVE_MANY_MUTATION,
      {
        where,
      },
    );
    return result;
  }
}
